import {StackScreenProps} from '@react-navigation/stack';
import {Box, Button, Toast} from 'native-base';
import React, {useEffect, useRef, useState} from 'react';
import {TextInput} from 'react-native';
import {ScrollView} from 'react-native-gesture-handler';
import {SafeAreaView} from 'react-native-safe-area-context';
import {t} from 'react-native-tailwindcss';
import {useBalanceQuery, useCreatePayoutMutation} from '../api/generated';
import {AmountInput} from '../components/AmountInput';
import {KeyboardAvoidingView} from '../components/KeyboardAvoidingView';
import {formatIntAmount} from '../lib/formatters';
import {triggerHapticFeedback} from '../lib/hapticFeedback';
import i18n from '../lib/i18n';
import {config} from '../stage.config';
import {Nav, NavParams} from './navigation';

export const PayoutScreen: React.FC<StackScreenProps<NavParams, Nav.PayoutScreen>> = ({
  navigation
}) => {
  const {data: balanceData} = useBalanceQuery();
  const [createPayout, {loading: isCreatingPayout}] = useCreatePayoutMutation();
  const balance = balanceData?.balance || 0;
  const scrollRef = useRef<ScrollView>(null);
  const inputRef = useRef<TextInput>(null);
  const [amount, setAmount] = useState(0);

  useEffect(() => {
    return navigation.addListener('transitionEnd', (nativeEvent) => {
      if (!nativeEvent.data.closing) {
        inputRef.current?.focus();
      }
    });
  }, [navigation]);

  const adjustAmount = () => {
    if (amount === balance) {
      setAmount(0);
      inputRef.current?.focus();
    } else {
      setAmount(balance);
      inputRef.current?.blur();
    }
  };

  const handleRecharge = async () => {
    if (amount > balance) return;
    await createPayout({variables: {amount}});
    triggerHapticFeedback();
    navigation.replace(Nav.HomeScreen);
    Toast.show({
      description: i18n.t('payoutSuccess'),
      duration: 3000
    });
  };

  const isValid = amount > 0 && amount <= balance;

  return (
    <SafeAreaView style={[t.flex1, t.bgWhite]} edges={['bottom']}>
      <KeyboardAvoidingView offset={125}>
        <ScrollView
          style={[t.flex1]}
          contentContainerStyle={[t.flexGrow]}
          keyboardDismissMode="interactive"
          ref={scrollRef}>
          <Box style={[t.flex1, t.justifyCenter]}>
            <AmountInput
              ref={inputRef}
              amount={amount}
              onError={() => scrollRef.current?.scrollToEnd()}
              onAmountChange={setAmount}
              onSubmit={handleRecharge}
              maxAmount={balance}
              hint={i18n.t('payoutMessage')}
              getMaxAmountErrorMessage={(maxAmountString) =>
                i18n.t('maxPayoutError', {amount: maxAmountString})
              }
            />
          </Box>
        </ScrollView>
        <Button style={[t.mX4]} variant="ghost" onPress={adjustAmount}>
          {amount === balance ? i18n.t('partialPayout') : i18n.t('fullPayout')}
        </Button>
        <Button
          style={[t.m4, t.mT4]}
          isDisabled={!isValid}
          onPress={handleRecharge}
          isLoading={isCreatingPayout}>
          {`${i18n.t('withdraw')} ${formatIntAmount(amount)}`}
        </Button>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};
